import { useEffect, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferred: BeforeInstallPromptEvent | null = null;

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    window.dispatchEvent(new Event("kaya-installable"));
  });
}

/**
 * Whether the browser has offered an install prompt, and the call that shows it. Shared by the
 * install Notice on Today and the Install row on Account & data, so both answer from the one
 * captured event.
 */
export function useInstallPrompt(): { canInstall: boolean; install: () => Promise<void> } {
  const [canInstall, setCanInstall] = useState(!!deferred);

  useEffect(() => {
    const on = () => setCanInstall(true);
    const off = () => setCanInstall(false);
    window.addEventListener("kaya-installable", on);
    window.addEventListener("appinstalled", off);
    return () => {
      window.removeEventListener("kaya-installable", on);
      window.removeEventListener("appinstalled", off);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    deferred = null;
    setCanInstall(false);
  };

  return { canInstall, install };
}
